import React, { useMemo } from "react";
import {
  AutoRenewButton,
  GracePeriodButton,
  ReactivateButton,
  RenewButton,
  SubscribeButton,
  UpgradeButton,
} from "scmp-sdk/components";
import { useSdkContext } from "scmp-sdk/components/contexts";
import type { SubscriptionButtonContainerProps } from "scmp-sdk/components/rosetta/subscription-button/styles";
import { SubscriptionButtonContainer } from "scmp-sdk/components/rosetta/subscription-button/styles";

import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";

import { EpaperButton } from "./epaper-button";

export type Props = {
  className?: string;
  fallback?: React.ReactNode;
  location?: "footer" | "masthead" | "print-button";
  variant?: SubscriptionButtonContainerProps["$variant"];
};
export const SubscriptionButton: React.FunctionComponent<Props> = ({
  className,
  fallback = null,
  location = "masthead",
  variant,
}) => {
  const { rosettaInstance } = useSdkContext();
  const isMobile = useResponsive(theme.breakpoints.only("mobile"), false);
  const mastheadButton = rosettaInstance?.rulesEngine.getOutcome("ui.masthead.button");

  const buttonType = useMemo(
    () =>
      mastheadButton?.getDisplayVariant({
        shouldIncludeEpaper: true,
        shouldIncludeScmpPlus: !isMobile,
      }),
    [isMobile, mastheadButton],
  );

  if (!rosettaInstance) {
    return null;
  }

  const handlerRenderButton = () => {
    switch (buttonType) {
      case "AUTORENEW":
        return <AutoRenewButton className={className} />;
      case "EPAPER":
        return <EpaperButton className={className} />;
      case "GRACEPERIOD":
        return <GracePeriodButton className={className} />;
      case "REACTIVATE":
        return <ReactivateButton className={className} location={location} />;
      case "RENEW":
        return <RenewButton className={className} />;
      case "SUBSCRIBE":
        return <SubscribeButton className={className} location={location} />;
      case "UPGRADE":
        return <UpgradeButton className={className} />;
      /* Fallback to my news if no subscription button is available */
      default:
        return fallback;
    }
  };

  return (
    <SubscriptionButtonContainer $variant={variant}>
      {handlerRenderButton()}
    </SubscriptionButtonContainer>
  );
};

SubscriptionButton.displayName = "SubscriptionButton";
